/**
 * StackCard - Individual card in the card deck view
 *
 * Displays a content item as a card with:
 * - Clean title + source attribution
 * - Scrollable markdown body (read mode) or textarea (edit mode)
 * - Word count + quality score
 * - Remove button
 *
 * Edit mode is controlled by the parent via the `isEditing` prop,
 * triggered by the toolbar Edit button (one consistent affordance).
 *
 * @module @humanizer/studio/components/workspace/StackCard
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { MathMarkdown } from '../markdown';
import type { StackItem } from '../../contexts/BufferSyncContext';

interface StackCardProps {
  item: StackItem;
  index: number;
  isEditing: boolean;
  isStarred?: boolean;
  onStar?: () => void;
  onEdit?: () => void;
  onRemove: () => void;
  onSave: (text: string) => void;
  onCancel: () => void;
  onDividerTitleChange?: (index: number, title: string) => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
}

export function StackCard({
  item, index, isEditing, isStarred, onStar, onEdit, onRemove, onSave, onCancel,
  onDividerTitleChange, onMoveLeft, onMoveRight, canMoveLeft, canMoveRight,
}: StackCardProps) {
  const [editText, setEditText] = useState('');
  const [editingDividerTitle, setEditingDividerTitle] = useState(false);
  const [dividerTitleText, setDividerTitleText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dividerInputRef = useRef<HTMLInputElement>(null);

  // Initialize editText when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditText(item.contentItem.text);
    }
  }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    onSave(editText);
  }, [editText, onSave]);

  const editWordCount = editText.split(/\s+/).filter(Boolean).length;

  // Start divider title editing
  const handleDividerTitleClick = useCallback(() => {
    setDividerTitleText(item.dividerTitle ?? 'Section');
    setEditingDividerTitle(true);
    setTimeout(() => dividerInputRef.current?.focus(), 0);
  }, [item.dividerTitle]);

  const handleDividerTitleSave = useCallback(() => {
    const trimmed = dividerTitleText.trim();
    if (trimmed && onDividerTitleChange) {
      onDividerTitleChange(index, trimmed);
    }
    setEditingDividerTitle(false);
  }, [dividerTitleText, index, onDividerTitleChange]);

  const handleDividerTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDividerTitleSave();
    } else if (e.key === 'Escape') {
      setEditingDividerTitle(false);
    }
  }, [handleDividerTitleSave]);

  // Divider card: simplified rendering with inline title editing
  if (item.isDivider) {
    return (
      <div className="stack-card stack-card--divider">
        {editingDividerTitle ? (
          <input
            ref={dividerInputRef}
            className="stack-card__divider-input"
            value={dividerTitleText}
            onChange={(e) => setDividerTitleText(e.target.value)}
            onBlur={handleDividerTitleSave}
            onKeyDown={handleDividerTitleKeyDown}
          />
        ) : (
          <button
            className="stack-card__divider-title"
            onClick={handleDividerTitleClick}
            title="Click to edit section title"
            aria-label={`Edit section: ${item.dividerTitle ?? 'Section'}`}
          >
            {item.dividerTitle ?? 'Section'}
          </button>
        )}
        <button
          className="stack-card__remove"
          onClick={onRemove}
          title="Remove divider"
          aria-label="Remove divider"
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div className="stack-card">
      <div className="stack-card__header">
        <span className="stack-card__title" title={item.summary}>
          {item.summary}
        </span>
        {item.source && (
          <span className="stack-card__source">{item.source}</span>
        )}
        {item.qualityScore != null && (
          <span className="stack-card__score">
            {Math.round(item.qualityScore * 100)}%
          </span>
        )}
        {onStar && (
          <button
            className={`stack-card__star${isStarred ? ' stack-card__star--active' : ''}`}
            onClick={onStar}
            title={isStarred ? 'Unstar (remove as search anchor)' : 'Star (use as search anchor)'}
            aria-label={isStarred ? 'Unstar card' : 'Star card'}
          >
            {isStarred ? '\u2605' : '\u2606'}
          </button>
        )}
        {!isEditing && onEdit && (
          <button
            className="stack-card__edit"
            onClick={onEdit}
            title="Edit card text"
            aria-label="Edit card"
          >
            &#x270E;
          </button>
        )}
        <button
          className="stack-card__remove"
          onClick={onRemove}
          title="Remove from stack"
          aria-label="Remove from stack"
        >
          &times;
        </button>
      </div>

      <div className="stack-card__body">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            className="stack-card__textarea"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            spellCheck={false}
          />
        ) : (
          <article className="workspace__article">
            <MathMarkdown>{item.contentItem.text}</MathMarkdown>
          </article>
        )}
      </div>

      <div className="stack-card__footer">
        {isEditing ? (
          <>
            <span className="stack-card__word-count">
              {editWordCount.toLocaleString()} words
            </span>
            <div className="stack-card__edit-actions">
              <button
                className="stack-card__edit-action stack-card__edit-action--save"
                onClick={handleSave}
              >
                Save
              </button>
              <button
                className="stack-card__edit-action stack-card__edit-action--cancel"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="stack-card__word-count">
              {item.wordCount.toLocaleString()} words
            </span>
            <div className="stack-card__move-actions">
              <button
                className="stack-card__move-btn"
                onClick={onMoveLeft}
                disabled={!canMoveLeft}
                title="Move left"
                aria-label="Move card left"
              >
                &#8592;
              </button>
              <button
                className="stack-card__move-btn"
                onClick={onMoveRight}
                disabled={!canMoveRight}
                title="Move right"
                aria-label="Move card right"
              >
                &#8594;
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
